import { FC, useState, useEffect, MouseEvent, useCallback } from 'react'
import ReactDataSheet from 'react-datasheet'
import { produce } from 'immer'

import hearingMessage from '../../../data/hearing-message.json'
import {
  ConfirmDialog,
  ErrorMessage,
  ErrorMessages,
  SupplierHearingHeader
} from '../../../components/parts'
import {
  CellValue,
  GridDataSate,
  GridElement,
  OtherCompaniesBuildingGrid
} from './OtherCompaniesBuildingGrid'
import { OtherCompaniesBuildingFooter } from './OtherCompaniesBuildingFooter'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import { GetHearingQuantityListRes, WorkQuantity } from '@gateway.Supplier'
import { GetSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/GetSupplierHearingWorkQuantityListGateway'
import { PutSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/PutSupplierHearingWorkQuantityListGateway'
import { useAppSelector, useAppDispatch, CommonActions, ConfirmReplyActions } from '../../../stores'

import './OtherCompaniesBuilding.css'
import { WritableDraft } from 'immer/dist/internal'
import { Authority } from '../../../gateway/system/Authority'
import { MessageProvider } from '../../../services'

const GRID_DATA_STATE: GridDataSate = {
  grid: [],
  workQuantityList: [],
  workQuantityListLastMonth: [],
  displayModes: []
}

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const YEARS = 6

// カレンダの活性状態を設定
const onDataModifiableFn = (i: number, simulationYm: string, displayStartYear: string, authority?: Authority) => {
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const year = new Date().getFullYear()
  const month = new Date().getMonth() + 1

  // シミュレーション年月度が過去の場合すべて非活性
  if (simulatedYear < year ||
    (simulatedYear === year && simulatedMonth < month)) {
    return true
  }

  // 過去月を非活性
  // 社内ユーザは過去月も入力可能とする
  if (authority === Authority.Builder || authority === Authority.Material || authority === Authority.Branch) {
    return false
  } else {
    // 表示開始年度から当年までの差分年数を算出する（年度は4月開始）
    let diffYear = year - Number(displayStartYear)
    if (month < 4) {
      // 1～3月は年度の10～12か月目（+9）に変換し、0始まりの配列位置にするため-1する
      diffYear = diffYear - 1
      return i < month + 9 - 1 + diffYear * 12
    } else {
      // 4月以降は4月を年度の1か月目（-3）として、0始まりの配列位置にするため-1する
      return i < month - 3 - 1 + diffYear * 12
    }
  }
}

/**
 * Create GridData
 * @param workQuantityList
 * @param simulationYm
 * @param nonCtrlFlg
 * @returns
 */
const createData = (
  workQuantityList: WorkQuantity[],
  simulationYm: string,
  nonCtrlFlg: boolean,
  displayStartYear: string,
  authority?: Authority
) => {
  const gridData: GridElement[][] = []

  // 労務数一覧が空の場合、合計行のみを作成する
  if (workQuantityList.length === 0) {
    for (let arr = 0; arr < 3; arr++) {
      gridData.push([])
      for (let i = 0; i < YEARS * MONTHS.length; i++) {
        gridData[arr][i] = {
          value: 0,
          readOnly: true
        }
      }
    }
    return gridData
  }

  for (let r = 0; r < workQuantityList.length; r++) {
    gridData[r] = []
    for (let fy = 0; fy < YEARS; fy++) {
      for (let m = 0; m < MONTHS.length; m++) {
        gridData[r][fy * 12 + m] = {
          value: null,
          readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, authority) || nonCtrlFlg
        }
      }
    }
  }

  workQuantityList.forEach((workQuantityObj, rowIndex) => {
    const workQuantityByMonthArr = workQuantityObj.workQuantityByMonth
    workQuantityByMonthArr.forEach((valByMonth, columnIndex) => {
      gridData[rowIndex][columnIndex].value = valByMonth
    })
  })

  // 合計の計算
  for (let arr = 1; arr <= 3; arr++) {
    // 合計のRowを追加
    gridData.push([])
    for (let i = 0; i < gridData[0].length; i++) {
      let sum = 0
      for (let j = 0; j < gridData.length - arr; j++) {
        if ((arr === 1 && workQuantityList[j].decisionKbn === '1') ||
            (arr === 2 && workQuantityList[j].decisionKbn === '2') ||
            (arr === 3)) {
          sum += Number(gridData[j][i].value) ?? 0
        }
      }
      gridData[gridData.length - 1][i] = {
        value: sum,
        readOnly: true
      }
    }
  }
  return gridData
}

const HEARING_QUANTITY_LIST_RES: GetHearingQuantityListRes = {
  simulationYm: '',
  supplierCd: '',
  supplierBranchCd: '',
  factoryCd: '',
  expenseCd: '',
  targetHearingKbn: null,
  addStsCd: '',
  addStsCdBeforeChange: null,
  addStsCdAfterChange: null,
  addSts: '',
  workQuantityList: []
}

export const OtherCompaniesBuilding: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('他社物件')

  const dispatch = useAppDispatch()

  // 登録状況
  const regStatus = useAppSelector((state) => state.confirmreply.regStatus)

  const {
    replyYm,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const replyStsCd = useAppSelector((state) => state.confirmreply.replyStsCd)
  const userProp = useAppSelector((state) => state.common.userProp)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  const [displayMode, setDisplayMode] = useState<'0' | '1'>('1')
  const [confirmDialogSignInShow, setConfirmDialogSignInShow] = useState(false)
  const [confirmDialogTempSaveShow, setConfirmDialogTempSaveShow] =
    useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const [getHearingQuantityListRes, setGetHearingQuantityListRes] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [getHearingQuantityListResLastMonth, setGetHearingQuantityListResLastMonth] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [gridDataSate, setGridDataSate] = useState(GRID_DATA_STATE)
  // 変更検知
  const [isChanged, setIsChanged] = useState(false)

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  // 過去月または(業者ユーザかつ回答済)または確認済または(支店ユーザかつ自支店以外)の場合、入力項目を非活性にする
  const nonCtrlFlg =
    lastMonthFlg ||
    (userProp?.authority === Authority.Supplier && replyStsCd === '5') ||
    replyStsCd === '7' ||
    (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  const handleOnAddRowBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        const girdCell: GridElement[] = []
        const monthValArr: number[] = []

        // 当月以前のセルを非活性に設定する
        for (let fy = 0; fy < YEARS; fy++) {
          for (let m = 0; m < MONTHS.length; m++) {
            girdCell.push({
              value: null,
              readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, displayStartYear, userProp?.authority)
            })
            monthValArr.push(0)
          }
        }

        draft.grid.splice(draft.grid.length - 3, 0, girdCell)

        // 労務数(新規行)
        const workQuantityObj: WorkQuantity = {
          branchCd: '',
          procurementNo: '000000000',
          subNo: '00',
          expenseSeq: '00',
          workerKbn: '0',
          // (他社物件)
          hearingKbn: '4',
          evaluation: '',
          constructionNm: '',
          constructionNmManual: '',
          otherCompany: '',
          constructionPartCd: null,
          processStsCd: null,
          delayLevelCd: null,
          newAddFlg: 1,
          notApplicableFlg: 0,
          hiddenFlg: 0,
          notDelFlg: 0,
          lostOrderFlg: 0,
          workQuantityByMonth: monthValArr,
          updDtm: null,
          delFlg: 0,
          decisionKbn: '',
          buildingDiscloseFlg: null,
          completedFlg: null,
          remark: null,
          count: null,
          usedAsCopySource: 0,
          isCopied: 0
        }

        draft.workQuantityList.push(workQuantityObj)
        draft.displayModes.push(true)
      })
    )
  }

  const handleOnDisplayCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].hiddenFlg = Number(selected)
      })
    )
  }

  const handleOnDeleteCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].delFlg = Number(selected)
      })
    )
  }

  const handleCellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    setIsChanged(true)
    if (isNaN(Number(arrayOfChanges[0]?.value))) return

    setGridDataSate(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          const preCellVal = Number(draft.grid[row][col].value)
          const currentCellVal = Number(value)
          draft.grid[row][col].value = value

          draft.workQuantityList[row].workQuantityByMonth[col] = currentCellVal
          const preCellTotalVal = Number(
            draft.grid[gridDataSate.grid.length - 1][col].value
          )
          // 決定小計
          if (draft.workQuantityList[row].decisionKbn === '1') {
            const preDecisionCellTotalVal = Number(
              draft.grid[gridDataSate.grid.length - 3][col].value
            )
            const currentDecisionCellTotalVal =
             preDecisionCellTotalVal - preCellVal + currentCellVal
            draft.grid[gridDataSate.grid.length - 3][col].value =
             currentDecisionCellTotalVal
          }
          // 予定小計
          if (draft.workQuantityList[row].decisionKbn === '2') {
            const preExpectationCellTotalVal = Number(
              draft.grid[gridDataSate.grid.length - 2][col].value
            )
            const currentExpectationCellTotalVal =
            preExpectationCellTotalVal - preCellVal + currentCellVal
            draft.grid[gridDataSate.grid.length - 2][col].value =
             currentExpectationCellTotalVal
          }
          // 総合計
          const currentCellTotalVal =
            preCellTotalVal - preCellVal + currentCellVal
          draft.grid[gridDataSate.grid.length - 1][col].value =
            currentCellTotalVal
        })
      })
    )
  }

  const handleOnOtherCompanyInputChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].otherCompany = val
      })
    )
  }

  const handleOnConstructionNmManualInputChange = (
    val: string,
    rowNo: number
  ) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].constructionNmManual = val
      })
    )
  }

  const handleOnBranchDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].branchCd = val
      })
    )
  }

  const handleOnHiddenBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    if (displayMode === '0') {
      setDisplayMode('1')
      setGridDataSate(
        produce((draft) => {
          gridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
            const lastRowIndex = gridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )
    }
    if (displayMode === '1') {
      setDisplayMode('0')
      setGridDataSate(
        produce((draft) => {
          gridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.lostOrderFlg === 0
            const lastRowIndex = gridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )
    }
  }

  // 決定区分変更
  const handleOnDecisionKbnDropDownChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].decisionKbn = val
        gridDataSate.workQuantityList.forEach((obj, rowIndex) => {
          const lastRowIndex = gridDataSate.grid.length - 1
          setTotalRowData(draft, rowIndex, lastRowIndex)
        })
      })
    )
  }

  /** 登録ボタン押下 */
  const handleOnSaveBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setConfirmDialogSignInShow(true)
  }

  /** 一時保存ボタン押下 */
  const handleOnTempSaveBtnClick = (
    _event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    setConfirmDialogTempSaveShow(true)
  }

  // 登録確認ダイアログOKボタン押下
  const dialogConfirmSignInBtn = () => {
    setConfirmDialogSignInShow(false)

    const newQuantityList = produce(getHearingQuantityListRes, (draft) => {
      draft.targetHearingKbn = ['4']
      draft.addStsCdBeforeChange = draft.addStsCd
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "3"(登録済) を設定する
      draft.addStsCdAfterChange = replyStsCd === '8' ? '4' : '3';
      // 業者ヒアリング(労務数)-明細登録用に表示開始年度を追加する
      // 業者ヒアリング(労務数)-明細登録はリクエストの型を業者ヒアリング(労務数)-明細取得と共用しているため
      // 明細登録用に表示開始年度は一時追加のプロパティとして設定する
      (draft as any).displayStartYear = displayStartYear

      const workQuantityList: WorkQuantity[] = []
      gridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 施工部位コード
          workQuantity.constructionPartCd = ''
          // 工程状況コード
          workQuantity.processStsCd = ''
          // 遅延程度コード
          workQuantity.delayLevelCd = ''
          // 対象外フラグ
          workQuantity.notApplicableFlg = 0
          // 備考
          workQuantity.remark = ''
          // 差分マーク
          workQuantity.diffMark = diff(gridDataSate, obj)

          workQuantityList.push(workQuantity)
        }
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '登録')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // 一時保存確認ダイアログOKボタン押下
  const dialogConfirmTempSaveBtn = () => {
    setConfirmDialogTempSaveShow(false)

    const newQuantityList = produce(getHearingQuantityListRes, (draft) => {
      draft.targetHearingKbn = ['4']
      draft.addStsCdBeforeChange = draft.addStsCd
      // 回答ステータスコードが"8"(事前調整)の場合は登録ステータスに "4"(事前調整) それ以外は "2"(一時保存) を設定する
      draft.addStsCdAfterChange = replyStsCd === '8' ? '4' : '2';
      // 業者ヒアリング(労務数)-明細登録用に表示開始年度を追加する
      // 業者ヒアリング(労務数)-明細登録はリクエストの型を業者ヒアリング(労務数)-明細取得と共用しているため
      // 明細登録用に表示開始年度は一時追加のプロパティとして設定する
      (draft as any).displayStartYear = displayStartYear

      const workQuantityList: WorkQuantity[] = []
      gridDataSate.workQuantityList.forEach((obj) => {
        // State.更新日時＝NULLかつState.削除フラグ=1(削除)の行は対象外とする
        if (!(obj.updDtm === null && obj.delFlg === 1)) {
          const workQuantity = Object.assign({}, obj)
          // 施工部位コード
          workQuantity.constructionPartCd = ''
          // 工程状況コード
          workQuantity.processStsCd = ''
          // 遅延程度コード
          workQuantity.delayLevelCd = ''
          // 対象外フラグ
          workQuantity.notApplicableFlg = 0
          // 備考
          workQuantity.remark = ''

          workQuantityList.push(workQuantity)
        }
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '一時保存')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  const fetchHearingQuantityList = useCallback(() => {
    dispatch(CommonActions.setIsLoading(true))
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "4"(他社物件)
      '4',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setGetHearingQuantityListRes(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // シミュレーション年月度の前月度データの取得
    const simulatedYear = Number(simulationYm.substring(0, 4))
    const simulatedMonth = Number(
      simulationYm.substring(4, simulationYm.length)
    )
    // 先月の取得
    const simulationYmLastMonth = getPreMonth(simulatedYear, simulatedMonth)

    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYmLastMonth,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      // "4"(他社物件)
      '4',
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setGetHearingQuantityListResLastMonth(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [expenseCd, factoryCd, simulationYm, supplierCd])

  // 差分情報の抽出
  const diff = (gridData: GridDataSate, workQuantity: WorkQuantity) => {
    let diffList: string[] = []
    const lastMonth = gridData.workQuantityListLastMonth.find((obj) =>
      obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq && obj.workerKbn === workQuantity.workerKbn)

    if (lastMonth === undefined || workQuantity.newAddFlg === 1) {
      diffList = new Array<string>(workQuantity.workQuantityByMonth.length).fill('1')
    } else {
      workQuantity.workQuantityByMonth.forEach((_, index) => {
        // シミュレーション年月度が4月度の場合
        // 変数．前月度労務能力．業者決定物件．月別労務数のIndexを+12する
        if (simulatedMonth === 4) {
          if (index + 12 <= workQuantity.workQuantityByMonth.length - 1) {
            diffList.push((workQuantity.workQuantityByMonth[index] ?? 0) !== (lastMonth?.workQuantityByMonth[index + 12] ?? 0) ? '1' : '0')
          } else {
            diffList.push('1')
          }
        } else {
          diffList.push((workQuantity.workQuantityByMonth[index] ?? 0) !== (lastMonth?.workQuantityByMonth[index] ?? 0) ? '1' : '0')
        }
      })
    }
    return diffList.join(',')
  }

  // #region useEffect functions start
  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      regStatus === undefined ||
      regStatus === null ||
      simulationYm === '' ||
      supplierCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === '' ||
      supplierBranchCd === ''

    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd
  ])

  useEffect(() => {
    fetchHearingQuantityList()
    return () => { dispatch(ConfirmReplyActions.setOtherCompanyBuildingLocation(0)) }
  }, [])

  useEffect(() => {
    setGridDataSate(
      produce((draft) => {
        const workQuantityList = getHearingQuantityListRes.workQuantityList
        const workQuantityListLastMonth = getHearingQuantityListResLastMonth.workQuantityList

        const formatedWorkQuantityList = workQuantityList.map((item) => ({
          ...item,
          delFlg: 0
        }))

        draft.grid = createData(
          formatedWorkQuantityList,
          simulationYm,
          nonCtrlFlg,
          displayStartYear,
          userProp?.authority
        )
        draft.workQuantityList = formatedWorkQuantityList
        draft.workQuantityListLastMonth = workQuantityListLastMonth

        const displayModes = workQuantityList.map((obj) => {
          return obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
        })
        draft.displayModes = displayModes
      })
    )
  }, [getHearingQuantityListRes.workQuantityList,
    getHearingQuantityListResLastMonth.workQuantityList,
    simulationYm])
  // #endregion useEffect functions end

  return (
    <>
      <SupplierHearingHeader
        regStatus={getHearingQuantityListRes.addStsCd}
        promptInfo={hearingMessage.otherCompaniesBuilding.headerPromptInfo}
        enableFixContentRegistration
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />
      <OtherCompaniesBuildingGrid
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        gridDataState={gridDataSate}
        nonCtrlFlg={nonCtrlFlg}
        onAddRowBtnClick={handleOnAddRowBtnClick}
        onDisplayCheckboxChange={handleOnDisplayCheckboxChange}
        onDeleteCheckboxChange={handleOnDeleteCheckboxChange}
        onCellsChanged={handleCellsChanged}
        onOtherCompanyInputChange={handleOnOtherCompanyInputChange}
        onConstructionNmManualInputChange={
          handleOnConstructionNmManualInputChange
        }
        onBranchDropDownChange={handleOnBranchDropDownChange}
        onDecisionKbnDropDownChange={handleOnDecisionKbnDropDownChange}
        expenseCd={expenseCd}
        displayStartYear={displayStartYear}
      />
      <OtherCompaniesBuildingFooter
        displayMode={displayMode}
        nonCtrlFlg={nonCtrlFlg}
        onHiddenBtnClick={handleOnHiddenBtnClick}
        onTempSaveBtnClick={handleOnTempSaveBtnClick}
        onSaveBtnClick={handleOnSaveBtnClick}
      />

      {confirmDialogSignInShow && (
        <ConfirmDialog
          title="登録"
          open={confirmDialogSignInShow}
          renderCloseButton
          id={'I007'}
          args={['登録']}
          onClickOk={dialogConfirmSignInBtn}
          onClickCancel={() => setConfirmDialogSignInShow(false)}
        />
      )}
      {confirmDialogTempSaveShow && (
        <ConfirmDialog
          title="一時保存"
          open={confirmDialogTempSaveShow}
          renderCloseButton
          id={'I007'}
          args={['一時保存']}
          onClickOk={dialogConfirmTempSaveBtn}
          onClickCancel={() => setConfirmDialogTempSaveShow(false)}
        />
      )}
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}

/**
 * @description 合計の計算
 * @param draft WritableDraft<GridDataSate>
 * @param currentRowIndex number
 * @param lastRowIndex number
 */
const setTotalRowData = (
  draft: WritableDraft<GridDataSate>,
  currentRowIndex: number,
  lastRowIndex: number
) => {
  for (let i = 0; i < draft.grid[lastRowIndex].length; i++) {
    if (currentRowIndex === 0) {
      // 総合計
      draft.grid[lastRowIndex][i].value = Number(
        draft.grid[currentRowIndex][i].value
      )
      draft.grid[lastRowIndex - 1][i].value = 0
      draft.grid[lastRowIndex - 2][i].value = 0
      // 決定小計
      if (draft.workQuantityList[currentRowIndex].decisionKbn === '1') {
        draft.grid[lastRowIndex - 2][i].value = Number(
          draft.grid[currentRowIndex][i].value
        )
      }
      // 予定小計
      if (draft.workQuantityList[currentRowIndex].decisionKbn === '2') {
        draft.grid[lastRowIndex - 1][i].value = Number(
          draft.grid[currentRowIndex][i].value
        )
      }
    } else {
      // 総合計
      draft.grid[lastRowIndex][i].value =
        Number(draft.grid[lastRowIndex][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
      // 決定小計
      if (draft.workQuantityList[currentRowIndex].decisionKbn === '1') {
        draft.grid[lastRowIndex - 2][i].value =
        Number(draft.grid[lastRowIndex - 2][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
      }
      // 予定小計
      if (draft.workQuantityList[currentRowIndex].decisionKbn === '2') {
        draft.grid[lastRowIndex - 1][i].value =
        Number(draft.grid[lastRowIndex - 1][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
      }
    }
  }
}

/**
 * 前月度の取得
 * @param year
 * @param month
 * @returns 前月度
 */
const getPreMonth = (year: number, month: number) => {
  let year2 = year
  let month2 = month - 1
  if (month2 === 0) {
    year2 = year2 - 1
    month2 = 12
  }

  let month2Str = `${month2}`
  if (month2 < 10) {
    month2Str = `0${month2}`
  }
  return `${year2}${month2Str}`
}
